import React from 'react'
import { connect } from 'react-redux'
import { Button, Form, Header, Icon, Label, Loader, Message, Modal, Segment, Table } from 'semantic-ui-react'

import {
  creditNoteBillingInvoice,
  fetchBillingInvoice,
  markBillingInvoicePaid,
  sendBillingInvoiceEmail,
  unmarkBillingInvoicePaid
} from 'actions/billing'
import { displayStatus, statusColor } from 'components/superuser/billing/invoiceStatus'

import { getBillingInvoice } from 'selectors/billing'

const today = () => new Date().toISOString().slice(0, 10)

class InvoiceDetail extends React.Component {
  state = { creditOpen: false, creditReason: '', error: null }

  componentDidMount () {
    this.props.fetch(this.props.invoiceId).catch((error) => this.setState({ error }))
  }

  handleMarkPaid = () => {
    const date = window.prompt('Paid on (YYYY-MM-DD):', today())
    if (date) this.props.markPaid(this.props.invoiceId, date)
  }

  handleCreditSubmit = async () => {
    await this.props.creditNote(this.props.invoiceId, this.state.creditReason)
    this.setState({ creditOpen: false, creditReason: '' })
  }

  render () {
    const { billingInvoice: inv } = this.props
    const { creditOpen, creditReason, error } = this.state

    if (error) return <Message negative><pre>{JSON.stringify(error, null, 2)}</pre></Message>
    if (!inv) return <Loader active inline="centered"/>

    const status = displayStatus(inv)
    const everSent = !!inv.issuedAt
    const isSending = inv.status === 'sending'
    const canSend = ['draft', 'issued', 'reminded'].includes(inv.status)
    const canMarkPaid = ['issued', 'reminded'].includes(inv.status)
    const canCredit = ['issued', 'reminded', 'paid'].includes(inv.status) && !inv.creditNoteReferenceInvoiceId

    return (
      <div>
        <Header as="h2">
          Invoice {inv.formattedIdentification || 'draft'}
          <Header.Subheader>
            <Label size="mini" color={statusColor(status)}>{status}</Label>
            {' · '} subscription #{inv.subscriptionId}
            {inv.payedAt && <> · paid on {inv.payedAt}</>}
            {everSent && <> · issued {new Date(inv.issuedAt).toLocaleDateString()}</>}
            {inv.lastRemindedAt && inv.lastRemindedAt !== inv.issuedAt &&
              <> · last contact {new Date(inv.lastRemindedAt).toLocaleDateString()}</>}
          </Header.Subheader>
        </Header>

        <Segment>
          {inv.pdfUrl && (
            <Button as="a" href={inv.pdfUrl} target="_blank">
              <Icon name="download"/> PDF
            </Button>
          )}
          {canSend && (
            <Button primary={!everSent} onClick={() => this.props.send(inv.id)}>
              <Icon name="mail"/> {everSent ? 'Send reminder' : 'Send invoice'}
            </Button>
          )}
          {isSending && <Button disabled loading>Sending…</Button>}
          {canMarkPaid && (
            <Button positive onClick={this.handleMarkPaid}>Mark paid</Button>
          )}
          {inv.status === 'paid' && (
            <Button onClick={() => this.props.unmarkPaid(inv.id)}>Unmark paid</Button>
          )}
          {canCredit && (
            <Button negative onClick={() => this.setState({ creditOpen: true })}>Issue credit note</Button>
          )}
        </Segment>

        <Segment>
          <Header as="h3">Line items</Header>
          <Table compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>#</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Qty</Table.HeaderCell>
                <Table.HeaderCell>Unit</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Unit price</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">VAT %</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {(inv.items || []).map((item) => (
                <Table.Row key={item.id}>
                  <Table.Cell>{item.position}</Table.Cell>
                  <Table.Cell>{item.description}</Table.Cell>
                  <Table.Cell textAlign="right">{item.quantity}</Table.Cell>
                  <Table.Cell>{item.unit}</Table.Cell>
                  <Table.Cell textAlign="right">{Number(item.price).toFixed(2)}</Table.Cell>
                  <Table.Cell textAlign="right">{item.vatPercentage}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>

        {inv.snapshot && (
          <Segment>
            <Header as="h3">Snapshot</Header>
            <pre style={{ fontSize: 11 }}>{JSON.stringify(inv.snapshot, null, 2)}</pre>
          </Segment>
        )}

        <Modal open={creditOpen} onClose={() => this.setState({ creditOpen: false })} size="small">
          <Modal.Header>Issue credit note</Modal.Header>
          <Modal.Content>
            <Form>
              <Form.Input label="Reason" required
                          value={creditReason}
                          onChange={(_, { value }) => this.setState({ creditReason: value })}/>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => this.setState({ creditOpen: false })}>Cancel</Button>
            <Button negative disabled={!creditReason} onClick={this.handleCreditSubmit}>Issue credit note</Button>
          </Modal.Actions>
        </Modal>
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  billingInvoice: getBillingInvoice(ownProps.invoiceId)(state)
})

const mapDispatchToProps = (dispatch) => ({
  fetch: (id) => dispatch(fetchBillingInvoice(id)),
  send: (id) => dispatch(sendBillingInvoiceEmail(id)),
  markPaid: (id, date) => dispatch(markBillingInvoicePaid(id, date)),
  unmarkPaid: (id) => dispatch(unmarkBillingInvoicePaid(id)),
  creditNote: (id, reason) => dispatch(creditNoteBillingInvoice(id, reason))
})

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceDetail)
